import { forwardRef } from 'react'
import {
  ACCENT,
  INK,
  MUTE,
  RULE,
  WHITE,
  FONT_STACK,
  DISPLAY_FONT,
  SERIF_FONT,
  documentStyle,
  pillBadgeStyle,
  formatUGX,
  formatDate,
} from './documentTokens'

/**
 * InvoiceDocument — the single source of truth for an SMA invoice.
 *
 * Design: minimalist corporate look matching the receipt — one terracotta
 * accent, hairline dividers, quiet labels, outlined pill badge. Fixed 980px
 * width matching invoice-redesign.html reference. Inherits tokens from
 * documentTokens.js.
 *
 * NOTE: This is a fixed-width document (980px). It does NOT use isMobile —
 * the invoice always renders at full landscape width, same as the receipt.
 */

const InvoiceDocument = forwardRef(function InvoiceDocument(
  { invoice },
  ref,
) {
  if (!invoice) return null

  const amount = parseFloat(invoice.amount || 0)
  const amountPaid = parseFloat(invoice.amount_paid || 0)
  const balance = amount - amountPaid
  const status = invoice.status || 'pending'

  const issuedDate = invoice.issued_date || invoice.created_at
  const studentName = invoice.student_name || '—'
  const studentNumber = invoice.student_number || invoice.student_id || '—'
  const studentEmail = invoice.student_email || invoice.email || ''
  const description = invoice.description || invoice.course_title || 'Course Fee'

  // Sentence-case status label for the pill badge
  const statusLabel = {
    paid: 'Paid',
    partial: 'Partial',
    pending: 'Pending',
    overdue: 'Overdue',
    confirmed: 'Confirmed',
    pendingVerify: 'Pending verification',
    unpaid: 'Unpaid',
  }[status] || status

  // Summary row — same pattern as the receipt's row function
  const srow = (label, value, opts = {}) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '16px',
        padding: '10px 0',
        borderBottom: opts.noDivider ? 'none' : `1px solid ${RULE}`,
        ...(opts.borderTop ? { borderTop: `1px solid ${RULE}` } : {}),
      }}
    >
      <span
        style={{
          fontSize: '13px',
          color: opts.accent ? ACCENT : MUTE,
          fontWeight: opts.accent ? '700' : '400',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: opts.accent ? '14.5px' : opts.bold ? '14px' : '13.5px',
          color: opts.accent ? ACCENT : INK,
          fontWeight: opts.bold || opts.accent ? '600' : '400',
          ...(opts.accent ? { fontWeight: '700' } : {}),
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  )

  return (
    <div
      ref={ref}
      className="invoice-document bg-white text-gray-900 mx-auto shadow-lg"
      style={{
        ...documentStyle(false, '980px'),
        width: '980px',
        minWidth: '980px',
        padding: '44px 56px 36px',
      }}
    >
      {/* ── Masthead — logo left, invoice meta right ─────────────────────── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingBottom: '20px',
        }}
      >
        {/* Logo + academy name */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
          }}
        >
          <img
            src="/Logos/SMA LOGO  VERSION 2-04.png"
            alt="Synergy Multimedia Academy"
            style={{
              height: '56px',
              width: 'auto',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <div style={{ lineHeight: 1.3, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: DISPLAY_FONT,
                fontWeight: '700',
                fontSize: '15px',
                color: INK,
                letterSpacing: '0.01em',
              }}
            >
              Synergy Multimedia Academy
            </p>
            <p
              style={{
                margin: '2px 0 0',
                fontSize: '10.5px',
                color: MUTE,
              }}
            >
              Kampala, Uganda
            </p>
          </div>
        </div>

        {/* Invoice meta — right-aligned label/value pairs */}
        <div
          style={{
            textAlign: 'right',
            fontSize: '11.5px',
            lineHeight: 1.9,
            flexShrink: 0,
          }}
        >
          <div>
            <span style={{ color: MUTE, marginRight: '8px' }}>Invoice</span>
            <span style={{ fontWeight: '600' }}>{invoice.invoice_number || '—'}</span>
          </div>
          <div>
            <span style={{ color: MUTE, marginRight: '8px' }}>Date</span>
            <span style={{ fontWeight: '600' }}>{formatDate(issuedDate)}</span>
          </div>
          <div>
            <span style={{ color: MUTE, marginRight: '8px' }}>Due</span>
            <span style={{ fontWeight: '600' }}>{formatDate(invoice.due_date)}</span>
          </div>
        </div>
      </div>

      {/* ── Title row — ink border-top, serif title, status pill ─────────── */}
      <div
        style={{
          borderTop: `1px solid ${INK}`,
          paddingTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: SERIF_FONT,
            fontSize: '24px',
            fontWeight: '400',
            color: INK,
            lineHeight: 1.2,
          }}
        >
          Invoice
        </h1>
        <span style={pillBadgeStyle(false, ACCENT)}>
          {statusLabel}
        </span>
      </div>

      {/* ── Bill to block ────────────────────────────────────────────────── */}
      <div style={{ marginTop: '28px' }}>
        <p
          style={{
            margin: '0 0 4px',
            fontSize: '11px',
            color: MUTE,
          }}
        >
          Bill to
        </p>
        <p
          style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: '600',
            color: INK,
            lineHeight: 1.3,
          }}
        >
          {studentName}
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontSize: '11.5px',
            color: MUTE,
            lineHeight: 1.4,
          }}
        >
          {studentNumber !== '—' ? `Student ID: ${studentNumber}` : ''}
          {studentNumber !== '—' && studentEmail ? ' · ' : ''}
          {studentEmail}
        </p>
      </div>

      {/* ── Items table — hairline dividers, no shaded header ────────────── */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: '28px',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                fontSize: '11px',
                color: MUTE,
                fontWeight: '400',
                padding: '0 0 10px 0',
                borderBottom: `1px solid ${INK}`,
              }}
            >
              Description
            </th>
            <th
              style={{
                textAlign: 'right',
                fontSize: '11px',
                color: MUTE,
                fontWeight: '400',
                padding: '0 0 10px 0',
                borderBottom: `1px solid ${INK}`,
              }}
            >
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                padding: '14px 0',
                fontSize: '13.5px',
                color: INK,
                borderBottom: `1px solid ${RULE}`,
              }}
            >
              {description}
            </td>
            <td
              style={{
                padding: '14px 0',
                fontSize: '13.5px',
                color: INK,
                textAlign: 'right',
                borderBottom: `1px solid ${RULE}`,
              }}
            >
              {formatUGX(amount)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Summary — right-aligned, hairline rows, accent only on balance ─ */}
      <div style={{ width: '340px', marginLeft: 'auto', marginTop: '20px' }}>
        {srow('Subtotal', formatUGX(amount), { borderTop: true })}
        {srow('Amount paid', formatUGX(amountPaid), { bold: true })}
        {srow('Balance due', formatUGX(balance), { accent: true, noDivider: true })}
      </div>

      {/* ── Footer — hairline top, left/right space-between ──────────────── */}
      <div
        style={{
          marginTop: '36px',
          paddingTop: '14px',
          borderTop: `1px solid ${RULE}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          fontSize: '10px',
          color: MUTE,
        }}
      >
        <span>Synergy Multimedia Academy — thank you for your business</span>
        <span>Due {formatDate(invoice.due_date)}</span>
      </div>
    </div>
  )
})

export default InvoiceDocument
